import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Users, CheckCircle, XCircle, MessageSquare, AlertTriangle,
  Download, Lock, Loader2, Eye, Activity, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPage = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    dashboard: null,
    users: [],
    satisfactions: [],
    suggestions: [],
    events: []
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEvents, setUserEvents] = useState([]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/verify`, { password });
      if (response.data.success) {
        setAuthenticated(true);
        toast.success('Accès administrateur accordé');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, usersRes, satisfactionsRes, suggestionsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/feedback/satisfactions`),
        axios.get(`${API}/feedback/suggestions`)
      ]);
      
      setData({
        dashboard: dashboardRes.data,
        users: usersRes.data,
        satisfactions: satisfactionsRes.data,
        suggestions: suggestionsRes.data
      });
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserEvents = async (userId) => {
    try {
      const response = await axios.get(`${API}/events/${userId}`);
      setUserEvents(response.data);
      setSelectedUser(userId);
    } catch (error) {
      console.error('Error fetching user events:', error);
      toast.error('Erreur lors du chargement des événements');
    }
  };

  const handleValidateUser = async (userId, validated) => {
    try {
      // Get admin email from localStorage (assuming it's stored during login)
      const adminEmail = localStorage.getItem('userEmail') || 'admin@vip-plus.com';
      
      await axios.post(`${API}/admin/validate-user`, {
        user_id: userId,
        validated: validated,
        admin_email: adminEmail
      });
      
      toast.success(validated ? 'Formation validée' : 'Validation retirée');
      fetchAllData(); // Refresh data
    } catch (error) {
      console.error('Error validating user:', error);
      toast.error('Erreur lors de la validation');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vip_plus_academy_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export CSV téléchargé');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#f2f2f2]" data-testid="admin-login">
        <Navbar />
        <div className="container mx-auto px-4 py-12 max-w-md">
          <Card>
            <CardHeader className="text-center">
              <div className="h-16 w-16 rounded-full bg-[#571925]/10 mx-auto mb-4 flex items-center justify-center">
                <Lock className="h-8 w-8 text-[#571925]" />
              </div>
              <CardTitle className="text-xl text-[#571925] font-['Outfit']">
                Espace Administrateur
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Entrez le mot de passe admin"
                    data-testid="admin-password-input"
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#ff9a52] hover:bg-[#e08540] text-white"
                  data-testid="admin-login-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Accéder'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { dashboard, users, satisfactions, suggestions } = data;

  return (
    <div className="min-h-screen bg-[#f2f2f2]" data-testid="admin-dashboard">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#571925] font-['Outfit']">
              Dashboard Administrateur
            </h1>
            <p className="text-gray-600">Suivi et pilotage de la plateforme</p>
          </div>
          <Button 
            onClick={handleExport}
            className="bg-[#ff9a52] hover:bg-[#e08540] text-white rounded-full"
            data-testid="export-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter CSV
          </Button>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
            <Card className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Utilisateurs</p>
                    <p className="text-3xl font-bold text-[#571925]">{dashboard.users_count}</p>
                  </div>
                  <Users className="h-10 w-10 text-[#cc5759]/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Quiz validés</p>
                    <p className="text-3xl font-bold text-green-600">{dashboard.quiz_validated}</p>
                  </div>
                  <CheckCircle className="h-10 w-10 text-green-600/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Quiz échoués</p>
                    <p className="text-3xl font-bold text-red-600">{dashboard.quiz_failed}</p>
                  </div>
                  <XCircle className="h-10 w-10 text-red-600/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Événements</p>
                    <p className="text-3xl font-bold text-[#571925]">{dashboard.events_count}</p>
                  </div>
                  <Activity className="h-10 w-10 text-[#cc5759]/30" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-white">
            <TabsTrigger value="users" className="data-[state=active]:bg-[#571925] data-[state=active]:text-white">
              <Users className="h-4 w-4 mr-2" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="satisfactions" className="data-[state=active]:bg-[#571925] data-[state=active]:text-white">
              <MessageSquare className="h-4 w-4 mr-2" />
              Satisfaction
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="data-[state=active]:bg-[#571925] data-[state=active]:text-white">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Signalements
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Liste des utilisateurs</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Créé le</TableHead>
                        <TableHead>Dernière connexion</TableHead>
                        <TableHead>Quiz complétés</TableHead>
                        <TableHead>Quiz validés</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Validation</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id} className="hover:bg-gray-50">
                          <TableCell className="font-medium">{user.email}</TableCell>
                          <TableCell>{new Date(user.created_at).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell>{new Date(user.last_login).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell>{user.quiz_completed || 0}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.quiz_passed > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {user.quiz_passed || 0}
                            </span>
                          </TableCell>
                          <TableCell>
                            {user.validated ? (
                              <div className="flex flex-col space-y-1">
                                <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 text-center">
                                  ✓ Validé
                                </span>
                                {user.validated_by && (
                                  <span className="text-xs text-gray-500">Par: {user.validated_by}</span>
                                )}
                                {user.validated_at && (
                                  <span className="text-xs text-gray-500">
                                    Le: {new Date(user.validated_at).toLocaleDateString('fr-FR')}
                                  </span>
                                )}
                              </div>
                            ) : user.quiz_passed > 0 ? (
                              <span className="px-2 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700">
                                ⚠ À valider
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Non validé
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm"
                              onClick={() => handleValidateUser(user.id, !user.validated)}
                              className={`${
                                user.validated 
                                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                                  : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                              }`}
                            >
                              {user.validated ? (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Validé
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Non validé
                                </>
                              )}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => fetchUserEvents(user.id)}
                              className="text-[#cc5759] hover:text-[#571925]"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Détails
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>

                {/* User Events Modal */}
                {selectedUser && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
                      <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Événements utilisateur</CardTitle>
                        <Button 
                          variant="ghost" 
                          onClick={() => setSelectedUser(null)}
                        >
                          ✕
                        </Button>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-[400px]">
                          <div className="space-y-2">
                            {userEvents.map((event, i) => (
                              <div key={i} className="p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-bold text-[#571925]">
                                    {event.parcours ? event.parcours.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Activité générale'}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(event.timestamp).toLocaleString('fr-FR')}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">
                                  Action : {event.event_type.replace(/_/g, ' ')}
                                </p>
                                {event.section && (
                                  <p className="text-sm text-gray-500">Section : {event.section}</p>
                                )}
                              </div>
                            ))}
                            {userEvents.length === 0 && (
                              <p className="text-center text-gray-500 py-8">Aucun événement</p>
                            )}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Satisfactions Tab */}
          <TabsContent value="satisfactions">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Questionnaires de satisfaction ({satisfactions.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Parcours</TableHead>
                        <TableHead>Conforme</TableHead>
                        <TableHead>Objectifs</TableHead>
                        <TableHead>Supports</TableHead>
                        <TableHead>Applicable</TableHead>
                        <TableHead>Améliorations</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {satisfactions.map((sat) => (
                        <TableRow key={sat.id}>
                          <TableCell>{new Date(sat.timestamp).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell className="font-medium">{sat.parcours_id}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              sat.conforme_attentes === 'Oui' ? 'bg-green-100 text-green-700' :
                              sat.conforme_attentes === 'Plutôt oui' ? 'bg-blue-100 text-blue-700' :
                              sat.conforme_attentes === 'Plutôt non' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {sat.conforme_attentes}
                            </span>
                          </TableCell>
                          <TableCell>{sat.objectifs_atteints}/5</TableCell>
                          <TableCell>{sat.supports_clairs}/5</TableCell>
                          <TableCell>{sat.applicable}</TableCell>
                          <TableCell className="max-w-xs truncate" title={sat.ameliorations}>
                            {sat.ameliorations || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {satisfactions.length === 0 && (
                    <p className="text-center text-gray-500 py-8">Aucune réponse de satisfaction</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Suggestions Tab */}
          <TabsContent value="suggestions">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Signalements ({suggestions.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Parcours</TableHead>
                        <TableHead>Étape</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {suggestions.map((sug) => (
                        <TableRow key={sug.id}>
                          <TableCell>{new Date(sug.timestamp).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              sug.type === 'bug' ? 'bg-red-100 text-red-700' :
                              sug.type === 'amelioration' ? 'bg-[#ff9a52]/20 text-[#e08540]' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {sug.type === 'amelioration' ? 'Amélioration' : 
                               sug.type === 'bug' ? 'Bug' : 'Incompréhension'}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">{sug.parcours}</TableCell>
                          <TableCell>{sug.etape}</TableCell>
                          <TableCell className="max-w-md">
                            <p className="truncate" title={sug.description}>{sug.description}</p>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {suggestions.length === 0 && (
                    <p className="text-center text-gray-500 py-8">Aucun signalement</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
